import React, { useEffect, useState, useRef } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import './datepickerStyles.css';
import HourPreferences from './HourPreferences';
import WeekdayPreferences from './WeekdayPreferences';
import TerminalPreferences from './TerminalPreferences';
import { editAutoBookContainerData, SaveApiResMessage } from '@/actions';
import { Icons } from '@/assets';
import { ReactSVG } from 'react-svg';
import { useNotify } from 'react-admin';
import { CustomCircularProgress } from '@/components/Loading/CustomCircularProgress';

const AutobookTimeDateSelector = ({
  settings,
  AutobookId,
  saving_identifier,
  clickConatiner,
  autobookMode,
  lfdDate,
  setHistoryList,
  activeSection,
  setActiveSection,
}) => {
  const [circleHourLoading, setCircleHourLoading] = useState(false);
  const [circleWeekdayLoading, setCircleWeekdayLoading] = useState(false);
  const [circleTerminalLoading, setCircleTerminalLoading] = useState(false);
  const [rangeDraft, setRangeDraft] = useState([null, null]);
  const [rangePriority, setRangePriority] = useState('High');
  const [selectedRanges, setSelectedRanges] = useState([]);
  const [weekdayPrefs, setWeekdayPrefs] = useState([]);
  const [terminalPrefs, setTerminalPrefs] = useState([]);
  const [hourSelections, setHourSelections] = useState([]);
  const [hourDraft, setHourDraft] = useState([null, null]);
  const [hourPriority, setHourPriority] = useState('High');

  const notify = useNotify();

  const prevHourSelections = useRef(null);
  const prevSelectedRanges = useRef(null);
  const prevWeekdayPrefs = useRef(null);
  const prevTerminalPrefs = useRef(null);

  useEffect(() => {
    const hasInitialData = prevHourSelections.current !== null;

    if (hasInitialData) {
      if (activeSection === 'hour') {
        setCircleHourLoading(true);
      }
      if (activeSection === 'weekday') {
        setCircleWeekdayLoading(true);
      }
      if (activeSection === 'terminal') {
        setCircleTerminalLoading(true);
      }
    }

    const data = {
      saving_identifier: saving_identifier,
      status: autobookMode.status ? 'ENABLED' : 'DISABLED',
      hour_preferences: hourSelections,
      weekly_preferences: selectedRanges,
      weekday_preferences: weekdayPrefs,
      terminal_preferences: terminalPrefs,
    };

    const hasChanges =
      hasInitialData &&
      (JSON.stringify(hourSelections) !==
        JSON.stringify(prevHourSelections.current) ||
        JSON.stringify(selectedRanges) !==
          JSON.stringify(prevSelectedRanges.current) ||
        JSON.stringify(weekdayPrefs) !==
          JSON.stringify(prevWeekdayPrefs.current) ||
        JSON.stringify(terminalPrefs) !==
          JSON.stringify(prevTerminalPrefs.current));

    if (AutobookId && saving_identifier && hasChanges) {
      editAutoBookContainerData({ AutobookId, data })
        .then((res) => {
          setHistoryList(res.autobook?.event_history.reverse());
          prevHourSelections.current = hourSelections;
          prevSelectedRanges.current = selectedRanges;
          prevWeekdayPrefs.current = weekdayPrefs;
          prevTerminalPrefs.current = terminalPrefs;
        })
        .catch((err) => {
          SaveApiResMessage({
            title: 'Error',
            message: err || 'Failed to update autobook preferences',
            mode: 'error',
          });
        })
        .finally(() => {
          setTimeout(() => {
            setCircleHourLoading(false);
            setCircleWeekdayLoading(false);
            setCircleTerminalLoading(false);
          }, 500);
        });
    } else if (hasInitialData && !hasChanges) {
      setTimeout(() => {
        setCircleHourLoading(false);
        setCircleWeekdayLoading(false);
        setCircleTerminalLoading(false);
      }, 100);
    }
  }, [
    terminalPrefs,
    weekdayPrefs,
    hourSelections,
    selectedRanges,
    settings,
    AutobookId,
    saving_identifier,
    activeSection,
    autobookMode.status,
    setHistoryList,
  ]);

  useEffect(() => {
    setTerminalPrefs(settings?.terminal_preferences);
    setWeekdayPrefs(settings?.weekday_preferences);
    setHourSelections(settings?.hour_preferences);
    setSelectedRanges(settings?.weekly_preferences);

    // Initialize refs with current settings values to prevent false positive changes
    if (settings) {
      prevHourSelections.current = settings?.hour_preferences;
      prevSelectedRanges.current = settings?.weekly_preferences;
      prevWeekdayPrefs.current = settings?.weekday_preferences;
      prevTerminalPrefs.current = settings?.terminal_preferences;
    }
  }, [clickConatiner, settings]);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  return (
    <div id="aytobookPreferences">
      <div className="autobook-wrapper">
        <div className="legend-container">
          <div className="legend-titles">
            <span className="legend-title">Autobook Preferences</span>
            <span className="legend-description">
              Set your preferred time slots, days, and terminals for
              auto-scheduling.
            </span>
          </div>
          <div className="legend">
            <div className="legend-item">
              <div className="box-green"></div> High
            </div>
            <div className="legend-item">
              <div className="box-orange"></div> Medium
            </div>
            <div className="legend-item">
              <div className="box-yellow"></div> Low
            </div>
            <div className="legend-item">
              <div className="box-red"></div> Exclude
            </div>
          </div>
        </div>

        <div className="accordion">
          <div
            className={`accordion-item ${
              activeSection === 'hour' ? 'active' : ''
            }`}>
            <div
              className={`accordion-header ${
                activeSection === 'hour' ? 'active' : ''
              }`}
              onClick={() => toggleSection('hour')}>
              <span className="selctedtextRow">
                Hour Preferences{'   '}|
                <div className="selctedtext">
                  {hourSelections?.length || 0}
                  {'  '}Selected
                  {circleHourLoading ? (
                    <span className="circle-loading">
                      <CustomCircularProgress className="w-4 h-4 border-primary-text" />
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </span>
              <span className="accordion-icon">
                {activeSection === 'hour' ? (
                  <ReactSVG className="arrow-icon" src={Icons.arrowTop} />
                ) : (
                  <ReactSVG className="arrow-icon" src={Icons.arrowDown} />
                )}
              </span>
            </div>
            {activeSection === 'hour' && (
              <div className="accordion-content">
                <HourPreferences
                  hourSelections={hourSelections}
                  hourDraft={hourDraft}
                  hourPriority={hourPriority}
                  setHourSelections={setHourSelections}
                  setHourDraft={setHourDraft}
                  setHourPriority={setHourPriority}
                />
              </div>
            )}
          </div>

          <div
            className={`accordion-item ${
              activeSection === 'weekday' ? 'active' : ''
            }`}>
            <div
              className={`accordion-header ${
                activeSection === 'weekday' ? 'active' : ''
              }`}
              onClick={() => toggleSection('weekday')}>
              <span className="selctedtextRow">
                Weekday Preferences{'   '}|
                <div className="selctedtext">
                  {selectedRanges?.length || 0}
                  {'  '}Selected
                  {circleWeekdayLoading ? (
                    <span className="circle-loading">
                      <CustomCircularProgress className="w-4 h-4 border-primary-text" />
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </span>
              <span className="accordion-icon">
                {activeSection === 'weekday' ? (
                  <ReactSVG className="arrow-icon" src={Icons.arrowTop} />
                ) : (
                  <ReactSVG className="arrow-icon" src={Icons.arrowDown} />
                )}
              </span>
            </div>
            {activeSection === 'weekday' && (
              <div className="accordion-content">
                <WeekdayPreferences
                  weekdayPrefs={weekdayPrefs}
                  setWeekdayPrefs={setWeekdayPrefs}
                  rangeDraft={rangeDraft}
                  setRangeDraft={setRangeDraft}
                  rangePriority={rangePriority}
                  setRangePriority={setRangePriority}
                  selectedRanges={selectedRanges}
                  setSelectedRanges={setSelectedRanges}
                  lfdDate={lfdDate}
                />
              </div>
            )}
          </div>

          <div
            className={`accordion-item ${
              activeSection === 'terminal' ? 'active' : ''
            }`}>
            <div
              className={`accordion-header ${
                activeSection === 'terminal' ? 'active' : ''
              }`}
              onClick={() => toggleSection('terminal')}>
              <span className="selctedtextRow">
                Terminal Preferences{'   '}|
                <div className="selctedtext">
                  {Object.values(terminalPrefs || {}).filter(
                    (item) =>
                      item?.priority !== null && item?.priority !== 'NONE'
                  ).length || 0}{' '}
                  Selected
                  {circleTerminalLoading ? (
                    <span className="circle-loading">
                      <CustomCircularProgress className="w-4 h-4 border-primary-text" />
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </span>
              <span className="accordion-icon">
                {activeSection === 'terminal' ? (
                  <ReactSVG className="arrow-icon" src={Icons.arrowTop} />
                ) : (
                  <ReactSVG className="arrow-icon" src={Icons.arrowDown} />
                )}
              </span>
            </div>
            {activeSection === 'terminal' && (
              <div className="accordion-content">
                <TerminalPreferences
                  terminalPrefs={terminalPrefs}
                  setTerminalPrefs={setTerminalPrefs}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default AutobookTimeDateSelector;
